import { useNavigate } from 'react-router-dom';
import { snackbar } from '@occc-app/design-system';
import { LinkItem } from '@occc/ui-design-system';
import { useOpenState } from '@occc-app/hooks/useOpenState';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { useOfferDeleteMutation } from '@occc-app/dataAccessLayer/offer/mutations';
import { OfferType } from '@occc-app/offerValidationService/types';
import { routeCreator as campaignRouteCreator } from '@occc-app/routes/Campaign/utils';
import { baseRouteCreator as campaignComponentsBaseRouteCreator } from '@occc-app/routes/CampaignComponents/utils';
import { createRouteCreator as programManagementRouteCreator } from '@occc-app/routes/ProgramManagement/utils';
import { OfferFragment } from '@occc/graphql';

import { useGetFormContainerProps } from './useGetFormContainerProps';
import { getTitleByOfferType } from '../utils';

type GetContainerPropsArgs = {
  campaignId?: string;
  offerId: string;
  offerType: OfferType;
  flowType: Flow_Types;
  getBreadcrumbLinks: (offerName: string) => LinkItem[];
  ignoreConfigureFormSubmit?: boolean;
  getIsOrderAheadEnabled?: (offer: OfferFragment | undefined) => boolean;
  finallyRedirectTo?: string;
};
export const useGetContainerProps = ({
  campaignId,
  offerId,
  offerType,
  flowType,
  getBreadcrumbLinks,
  ignoreConfigureFormSubmit = false,
  getIsOrderAheadEnabled,
  finallyRedirectTo,
}: GetContainerPropsArgs) => {
  const navigate = useNavigate();
  const [open, openModal, closeModal] = useOpenState();
  const offerTypeTitle = getTitleByOfferType(offerType);

  const baseRoute = campaignId
    ? campaignRouteCreator.details(campaignId)
    : programManagementRouteCreator(offerType).base();

  const offerDetailsRoute =
    finallyRedirectTo ||
    (campaignId
      ? campaignComponentsBaseRouteCreator.details(campaignId, offerId)
      : programManagementRouteCreator(offerType).details(offerId));

  const onDeleteCompleted = () => {
    const offerTypeTitle = getTitleByOfferType(offerType);
    snackbar.success(`${offerTypeTitle} has been deleted.`);
    navigate(baseRoute);
  };
  const [offerDelete] = useOfferDeleteMutation({
    onCompleted: onDeleteCompleted,
  });
  const handleDelete = async () => {
    await offerDelete({
      variables: {
        id: offerId,
      },
    });
  };

  const redirectToOfferDetails = () => {
    navigate(offerDetailsRoute);
  };

  const handleCancelClick = (hasUnsavedChanges: boolean) => {
    hasUnsavedChanges ? openModal() : redirectToOfferDetails();
  };

  const dialogProps = {
    open,
    closeModal,
    offerTypeTitle,
    onConfirmClick: flowType === Flow_Types.Configure ? handleDelete : redirectToOfferDetails,
  };

  const { layoutProps, initialFormProps, formContainersProps, scheduleFormProps } =
    useGetFormContainerProps({
      campaignId,
      offerId,
      offerDetailsRoute,
      onCancel: flowType === Flow_Types.Configure ? openModal : handleCancelClick,
      currentFlowType: flowType,
      offerType,
      getBreadcrumbLinks,
      ignoreConfigureFormSubmit,
      getIsOrderAheadEnabled,
    });

  return {
    layoutProps,
    initialFormProps,
    scheduleFormProps,
    formContainersProps,
    dialogProps,
  };
};
